/* eslint-disable no-unused-vars */
/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { Canvas, Group, Line, Rect, ActiveSelection } from 'fabric';

import { MetaBullet } from '../classes/MetaBullet';
import { TextEntity } from '../classes/TextEntity';
import { Marker } from '../classes/Marker';
import { TextEntityType } from '../../types/fabric-extensions';
import { generateObjectId } from '../../utils/generateId';
import { useCanvasStore } from '../../store';
import {
  isSelectionOnlyMarkers,
  hasMarkersInMixedSelection,
  lockMarkerTransformations,
} from '../../utils/metaBullets/markerSelectionUtils';

import { MetaBulletBackupManager } from './MetaBulletBackupManager';

/**
 * Центральная система управления метабуллитами
 * Управляет всеми MetaBullet объектами на canvas
 */
export class MetaBulletSystem {
  private canvas: Canvas;
  private canvasId: string;
  private isActiveSelectionMode: boolean = false; // флаг режима ActiveSelection
  private metaBullets: Map<string, MetaBullet> = new Map();
  private isInitialized = false;
  private isInitializing = false;
  public hasPositionChanges = false; // Публичный доступ для проверки необходимости бэкапа
  private isIgnoringObjectRemovals = false; // Флаг для временного отключения обработки удаления объектов
  private isProcessingSmartSelection = false; // Флаг для предотвращения рекурсии умного выделения

  // ✅ Менеджер бекапов
  private backupManager: MetaBulletBackupManager | null = null;

  // 🔄 Координация операций между метабуллитами
  private isProcessingGlobalOperation = false;
  private globalOperationBackupScheduled = false;

  // Сохраняем bound функции для правильной очистки event listeners
  private boundHandlers = {
    handleEntityChanged: this.handleEntityChanged.bind(this),
    handleObjectRemoved: this.handleObjectRemoved.bind(this),
    handleMouseDown: this.handleMouseDown.bind(this),
    handleObjectMoving: this.handleObjectMoving.bind(this),
    handleMouseUp: this.handleMouseUp.bind(this),
    handleSelectionCreated: this.handleSelectionCreated.bind(this),
    handleSelectionUpdated: this.handleSelectionUpdated.bind(this),
    handleSelectionCleared: this.handleSelectionCleared.bind(this),
  };

  // Новые свойства для визуализации границ
  private draggedEntity: TextEntity | null = null;
  private draggedEntityOriginalMetaBullet: MetaBullet | null = null;
  private metaBulletBoundaryRect: Group | null = null;
  private isDragOutOfBounds = false;
  private dragThresholdOut = 50; // Порог для выхода за границы
  private dragThresholdIn = 30; // Порог для возврата в границы (гистерезис)

  // Отслеживание реального движения объекта
  private draggedEntityOriginalPosition: { left: number; top: number } | null = null;
  private hasActuallyMoved = false;

  // Новые свойства для объединения метабуллитов
  private targetMetaBullet: MetaBullet | null = null;
  private targetMetaBulletBoundaryRect: Group | null = null;
  private isOverTargetMetaBullet = false;

  // Оригинальные границы метабуллита до начала драга
  private originalMetaBulletBounds: {
    minX: number;
    minY: number;
    maxX: number;
    maxY: number;
  } | null = null;

  // Визуальное выделение MetaBullet при редактировании (теперь используется hover)

  // Визуальное выделение MetaBullet при наведении мыши
  private hoverHighlightRect: Rect | null = null;
  private currentHoveredMetaBulletId: string | null = null;

  // Отслеживание ActiveSelection
  private activeSelectionEntities: Set<string> = new Set();
  private isActiveSelectionMoving = false;

  constructor(canvas: Canvas, canvasId: string) {
    this.canvas = canvas;
    this.canvasId = canvasId;

    // console.log('🚀 MetaBulletSystem initialized for canvas:', canvasId);
  }

  /**
   * Инициализировать BackupManager
   */
  initializeBackupManager(
    addBackup: (canvasId: string, state: unknown) => void,
    checkUndoRedoInProgress?: () => boolean,
  ): void {
    this.backupManager = new MetaBulletBackupManager(
      this.canvas,
      this.canvasId,
      addBackup,
      checkUndoRedoInProgress,
    );
  }

  /**
   * Получить BackupManager (для внешнего доступа)
   */
  getBackupManager(): MetaBulletBackupManager | null {
    return this.backupManager;
  }

  /**
   * Инициализация системы - настройка обработчиков событий
   */
  initialize(): void {
    if (this.isInitialized) {
      console.warn('⚠️ MetaBulletSystem already initialized');
      return;
    }

    this.setupEventListeners();
    this.isInitialized = true;

    // console.log('✅ MetaBulletSystem event listeners setup completed');
  }

  /**
   * Начать процесс инициализации (загрузка данных с бэкенда)
   */
  startInitialization(): void {
    // console.log('🔄 Starting MetaBullet system initialization...');
    this.isInitializing = true;
    this.hasPositionChanges = false;

    // ✅ Начинаем операцию инициализации в BackupManager
    if (this.backupManager) {
      this.backupManager.startOperation('initialization');
    }
  }

  /**
   * Завершить процесс инициализации и создать бекап если нужно
   */
  finishInitialization(options?: { skipBackup?: boolean }): void {
    const skipBackup = options?.skipBackup || false;

    this.isInitializing = false;

    // ✅ Завершаем операцию инициализации в BackupManager
    if (this.backupManager) {
      // ⚠️ ВАЖНО: Не создаем бэкап если прогрессивная загрузка еще идет!
      // Бэкап будет создан после завершения загрузки изображений
      if (this.hasPositionChanges && !skipBackup) {
        this.backupManager.requestBackup();
      }
      this.backupManager.finishOperation();
    } else {
      // Fallback к старой логике если BackupManager не инициализирован
      if (this.hasPositionChanges && !skipBackup) {
        this.canvas.fire('metaBullet:backup:needed', {
          reason: 'initialization_completed',
          hasChanges: true,
          metaBulletsCount: this.metaBullets.size,
          timestamp: Date.now(),
        });
      }
    }
  }

  /**
   * Отметить что были изменения позиций (для решения о бекапе)
   */
  markPositionChanges(): void {
    this.hasPositionChanges = true;
  }

  /**
   * Проверить находится ли система в процессе инициализации
   */
  get isInInitialization(): boolean {
    return this.isInitializing;
  }

  /**
   * Включить/выключить режим игнорирования удаления объектов
   *
   * ⚠️ КРИТИЧЕСКИ ВАЖНО: Этот метод используется для предотвращения случайного удаления
   * MetaBullet-ов во время массовых операций с canvas.
   *
   * ПРОБЛЕМА: При вызове canvas.clear() (например, в reorderCanvasObjects),
   * все объекты удаляются с canvas и для каждого триггерится событие 'object:removed'.
   * Наш handleObjectRemoved() ловит эти события и удаляет MetaBullet-ы из Map,
   * считая что объекты действительно удалены пользователем.
   *
   * РЕШЕНИЕ: Временно отключаем обработку событий 'object:removed' во время
   * технических операций типа reorderCanvasObjects или других массовых манипуляций.
   *
   * ИСПОЛЬЗОВАНИЕ:
   * 1. Вызвать setIgnoreObjectRemovals(true) ПЕРЕД canvas.clear()
   * 2. Выполнить массовую операцию (clear, add объектов обратно)
   * 3. Вызвать setIgnoreObjectRemovals(false) ПОСЛЕ завершения операции
   *
   * @param ignore - true для включения режима игнорирования, false для отключения
   */
  setIgnoreObjectRemovals(ignore: boolean): void {
    this.isIgnoringObjectRemovals = ignore;
  }

  /**
   * Получить перетаскиваемую сущность (для проверки в TextEntity)
   */
  get currentDraggedEntity(): TextEntity | null {
    return this.draggedEntity;
  }

  /**
   * Проверить находится ли перетаскиваемая сущность за границами (для проверки в TextEntity)
   */
  get isCurrentlyDragOutOfBounds(): boolean {
    return this.isDragOutOfBounds;
  }

  /**
   * Создать новый метабуллит из массива сущностей
   * @param entities - TextEntity объекты которые станут основой метабуллита
   * @param predefinedId - Предопределенный ID метабуллита (для загрузки с бэкенда)
   * @returns ID созданного метабуллита
   */
  createMetaBullet(entities: TextEntity[], predefinedId?: string): string {
    if (entities.length === 0) {
      throw new Error('Cannot create MetaBullet without entities');
    }

    // console.log(
    //   '🆕 Creating MetaBullet from entities:',
    //   entities.map((e) => e.id),
    //   predefinedId ? `with predefined ID: ${predefinedId}` : '',
    // );

    // Создаем новый метабуллит с первой сущностью
    const metaBullet = new MetaBullet(this.canvasId, entities[0].id, predefinedId);

    // Устанавливаем связь первой сущности с метабуллитом
    entities[0].metaBulletId = metaBullet.id;
    entities[0].entityOrder = 0;

    // Добавляем остальные сущности БЕЗ пересчета позиций
    for (let i = 1; i < entities.length; i++) {
      const entity = entities[i];

      // Устанавливаем связь с метабуллитом
      entity.metaBulletId = metaBullet.id;
      entity.entityOrder = i;

      // Добавляем в массив entityIds
      metaBullet.entityIds.push(entity.id);
    }

    // Пересчитываем позиции ОДИН раз для всех сущностей
    // Но только если не в процессе инициализации
    if (!this.isInitializing) {
      metaBullet.recalculatePositions(true);
    }

    // Сохраняем метабуллит в коллекции
    this.metaBullets.set(metaBullet.id, metaBullet);
    // console.debug('📥 MetaBullet added to map:', {
    //   metaBulletId: metaBullet.id,
    //   systemId: this.canvasId,
    //   mapSizeAfterAdd: this.metaBullets.size,
    //   allMetaBulletIds: Array.from(this.metaBullets.keys()),
    // });

    // console.debug('✅ MetaBullet created:', {
    //   debugInfo: metaBullet.getDebugInfo(),
    //   systemId: this.canvasId,
    //   totalMetaBulletsInSystem: this.metaBullets.size,
    // });
    return metaBullet.id;
  }

  /**
   * Создать новую текстовую сущность в существующем метабуллите
   * @param metaBulletId - ID метабуллита
   * @param afterEntityId - ID сущности, после которой вставить новую (опционально)
   * @param text - Текст для новой сущности (по умолчанию placeholder)
   * @param entityType - Тип сущности (по умолчанию 'bullet-level-1')
   * @param skipInitialRecalculation - Пропустить автоматическую рекалькуляцию (для случаев когда маркер будет добавлен позже)
   * @returns Созданная TextEntity
   */
  createEntityInMetaBullet(
    metaBulletId: string,
    afterEntityId?: string,
    text: string = '/',
    entityType: TextEntityType = 'bullet-level-1',
    skipInitialRecalculation = false,
  ): TextEntity {
    const metaBullet = this.metaBullets.get(metaBulletId);
    if (!metaBullet) {
      throw new Error(`MetaBullet with id ${metaBulletId} not found`);
    }

    // console.log('📝 Creating new entity in MetaBullet:', {
    //   metaBulletId,
    //   afterEntityId,
    //   text,
    // });

    // Определяем позицию для новой сущности
    const entities = metaBullet.getEntities();
    let insertPosition = { x: 100, y: 100 }; // Дефолтная позиция
    let insertIndex = entities.length; // По умолчанию в конец

    if (afterEntityId) {
      const afterEntity = entities.find((e) => e.id === afterEntityId);
      if (afterEntity) {
        const afterIndex = entities.indexOf(afterEntity);
        insertIndex = afterIndex + 1;

        // Позиция новой сущности под указанной
        insertPosition = {
          x: afterEntity.left || 100,
          y:
            (afterEntity.top || 100) + (afterEntity.height || 0) + (afterEntity.marginBottom || 16),
        };
      }
    } else if (entities.length > 0) {
      // Если не указано после какой сущности, то в конец
      const lastEntity = entities[entities.length - 1];
      insertPosition = {
        x: lastEntity.left || 100,
        y: (lastEntity.top || 100) + (lastEntity.height || 0) + (lastEntity.marginBottom || 16),
      };
    }

    // Генерируем уникальный ID для новой сущности используя стандартную функцию
    const entityId = generateObjectId(`entity-${entityType}`);

    // Получаем layer и stylesConfig из исходной сущности, если указана
    let layerToUse;
    let stylesConfigToUse;
    if (afterEntityId) {
      const afterEntity = entities.find((e) => e.id === afterEntityId);
      layerToUse = afterEntity?.layer;
      stylesConfigToUse = (afterEntity as any)?.stylesConfig;
    }

    // Создаем новую сущность
    const newEntity = new TextEntity(text, {
      id: entityId, // Передаем сгенерированный ID
      entityType,
      metaBulletId,
      entityOrder: insertIndex,
      left: insertPosition.x,
      top: insertPosition.y,
      width: 200, // Временная ширина (будет обновлена если передан afterEntityId)
      ...(layerToUse && { layer: layerToUse }), // Передаем layer если он есть
      ...(stylesConfigToUse && { stylesConfig: stylesConfigToUse }), // ✅ Передаем stylesConfig ДО создания!
    });

    // КРИТИЧНО: Устанавливаем флаг СРАЗУ после создания, ДО любых set() и ДО добавления на canvas
    // Это блокирует бекапы от событий modified/entity_modified которые могут сработать
    (newEntity as any)._isNewlyCreated = true;
    // ДОПОЛНИТЕЛЬНО: Временно блокируем бекапы через excludeFromBackup
    // Это гарантирует что бекап не создастся даже если событие modified сработает
    newEntity.excludeFromBackup = true;

    // Определяем исходную сущность для копирования свойств
    let sourceEntity: TextEntity | undefined;
    if (afterEntityId) {
      sourceEntity = entities.find((e) => e.id === afterEntityId);
    } else if (entities.length > 0) {
      // Если не указано afterEntityId, используем последнюю сущность
      sourceEntity = entities[entities.length - 1];
    }

    // Копируем свойства из исходной сущности (afterEntity или последняя сущность)
    if (sourceEntity) {
      // Копируем ширину (может вызвать событие modified, но флаг уже установлен)
      if (sourceEntity.width) {
        newEntity.set('width', sourceEntity.width);
      }

      // Копируем layer (может вызвать событие modified, но флаг уже установлен)
      if (sourceEntity.layer) {
        newEntity.set('layer', sourceEntity.layer);
      }

      // Копируем zIndex из исходной сущности
      if (sourceEntity.zIndex !== undefined && sourceEntity.zIndex !== null) {
        newEntity.set('zIndex', sourceEntity.zIndex);
      }
    }

    // Добавляем на canvas
    this.canvas.add(newEntity);

    //  Добавляем новую TextEntity в систему слоёв для корректной работы Up/Down Layer
    try {
      const canvasStore = useCanvasStore.getState();
      const activeCanvasId = canvasStore.activeCanvasId;

      if (activeCanvasId && newEntity.layer) {
        canvasStore.addLayerObject(activeCanvasId, newEntity.layer, {
          object: newEntity,
          zIndex: newEntity.zIndex || 0,
          layer: newEntity.layer,
        });
      }
    } catch (error) {
      console.warn('⚠️ Не удалось добавить новую TextEntity в layersMap:', error);
    }

    // Добавляем в метабуллит (пропускаем рекалькуляцию если указано)
    metaBullet.addEntity(newEntity, insertIndex, skipInitialRecalculation);

    // console.log('✅ New entity created:', newEntity.getDebugInfo());
    return newEntity;
  }

  /**
   * Удаляет сущность из метабуллита и с canvas
   * @param entityId - ID сущности для удаления
   * @returns ID предыдущей сущности для переключения фокуса (если есть)
   */
  removeEntityFromMetaBullet(entityId: string): string | null {
    // Находим сущность на canvas
    const entity = this.canvas.getObjects().find((obj) => obj.id === entityId) as TextEntity;
    if (!entity || !entity.isMetaBulletEntity) {
      console.warn('⚠️ Сущность не найдена или не является метабуллит сущностью:', entityId);
      return null;
    }

    const metaBulletId = entity.metaBulletId;
    const metaBullet = this.getMetaBullet(metaBulletId);
    if (!metaBullet) {
      console.warn('⚠️ MetaBullet не найден:', metaBulletId);
      return null;
    }

    // Проверяем, есть ли сущность в метабуллите (может быть уже удалена через handleObjectRemoved)
    const entities = metaBullet.getEntities();
    const currentIndex = entities.findIndex((e) => e.id === entityId);

    if (currentIndex === -1) {
      // Сущность уже удалена из MetaBullet, просто удаляем с canvas
      this.canvas.remove(entity);
      return null; // Не можем найти предыдущую сущность
    }

    const previousEntity = currentIndex > 0 ? entities[currentIndex - 1] : null;

    // Удаляем сущность из метабуллита (передаем ID, а не объект)
    metaBullet.removeEntity(entity.id);

    // Удаляем с canvas
    this.canvas.remove(entity);

    // Удаляем TextEntity из layersMap
    try {
      const canvasStore = useCanvasStore.getState();
      const activeCanvasId = canvasStore.activeCanvasId;

      if (activeCanvasId && entity.layer) {
        canvasStore.removeLayerObject(activeCanvasId, entity.layer, {
          object: entity,
          zIndex: entity.zIndex || 0,
          layer: entity.layer,
        });
      }
    } catch (error) {
      console.warn('⚠️ Не удалось удалить TextEntity из layersMap:', error);
    }

    // Возвращаем ID предыдущей сущности для переключения фокуса
    return previousEntity?.id || null;
  }

  /**
   * Получить метабуллит по ID
   */
  getMetaBullet(metaBulletId: string): MetaBullet | undefined {
    const result = this.metaBullets.get(metaBulletId);
    // console.debug('🔍 Getting MetaBullet by ID:', {
    //   metaBulletId,
    //   systemId: this.canvasId,
    //   found: !!result,
    //   totalMetaBullets: this.metaBullets.size,
    //   availableIds: Array.from(this.metaBullets.keys()),
    // });
    return result;
  }

  /**
   * Получить все метабуллиты
   */
  getAllMetaBullets(): MetaBullet[] {
    return Array.from(this.metaBullets.values());
  }

  /**
   * Проверить активен ли режим ActiveSelection
   */
  public get isInActiveSelectionMode(): boolean {
    return this.isActiveSelectionMode;
  }

  /**
   * Восстанавливает позиции всех MetaBullet после снятия ActiveSelection
   */
  private restoreMetaBulletPositionsAfterActiveSelection(): void {
    // Небольшая задержка для завершения снятия выделения
    setTimeout(() => {
      this.metaBullets.forEach((metaBullet) => {
        // Обновляем якорную позицию первой сущности после перемещения
        const entities = metaBullet.getEntities();
        if (entities.length > 0) {
          // Сбрасываем старый якорь и устанавливаем новый на текущую позицию
          metaBullet.resetAnchor();
        }
        metaBullet.recalculatePositions(false); // Без анимации для быстроты
      });
    }, 10);
  }

  /**
   * Найти метабуллит по ID сущности
   */
  findMetaBulletByEntityId(entityId: string): MetaBullet | undefined {
    for (const metaBullet of this.metaBullets.values()) {
      if (metaBullet.entityIds.includes(entityId)) {
        return metaBullet;
      }
    }
    return undefined;
  }

  /**
   * Удалить метабуллит и все его сущности
   */
  removeMetaBullet(metaBulletId: string): void {
    const metaBullet = this.metaBullets.get(metaBulletId);
    if (!metaBullet) return;

    // console.log('🗑️ Removing MetaBullet:', metaBulletId);

    // Удаляем все сущности с canvas
    const entities = metaBullet.getEntities();
    entities.forEach((entity) => {
      this.canvas.remove(entity);
    });

    // Удаляем метабуллит из коллекции
    this.metaBullets.delete(metaBulletId);

    // console.log('✅ MetaBullet removed:', metaBulletId);
  }

  /**
   * Очистка системы
   */
  destroy(): void {
    console.log('💥 Destroying MetaBulletSystem:', {
      systemId: this.canvasId,
      metaBulletsCount: this.metaBullets.size,
      metaBulletIds: Array.from(this.metaBullets.keys()),
    });

    // Удаляем все обработчики событий
    this.removeEventListeners();

    // Скрываем подсветку при наведении если была активна
    this.hideHoverHighlight();

    // Очищаем коллекцию метабуллитов
    this.metaBullets.clear();

    this.isInitialized = false;
  }

  /**
   * Настройка обработчиков событий
   */
  private setupEventListeners(): void {
    // Используем сохраненные bound функции
    // Обработка событий от TextEntity
    this.canvas.on('metaBullet:entity:changed', this.boundHandlers.handleEntityChanged);

    // Обработка удаления объектов
    this.canvas.on('object:removed', this.boundHandlers.handleObjectRemoved);

    // Новые обработчики для drag & drop
    this.canvas.on('mouse:down', this.boundHandlers.handleMouseDown);
    this.canvas.on('object:moving', this.boundHandlers.handleObjectMoving);
    this.canvas.on('mouse:up', this.boundHandlers.handleMouseUp);

    // Обработчики для ActiveSelection
    this.canvas.on('selection:created', this.boundHandlers.handleSelectionCreated);
    this.canvas.on('selection:updated', this.boundHandlers.handleSelectionUpdated);
    this.canvas.on('selection:cleared', this.boundHandlers.handleSelectionCleared);
  }

  /**
   * Удаление обработчиков событий
   */
  private removeEventListeners(): void {
    // 🎯 ИСПРАВЛЕНИЕ: Используем сохраненные bound функции
    this.canvas.off('metaBullet:entity:changed', this.boundHandlers.handleEntityChanged);
    this.canvas.off('object:removed', this.boundHandlers.handleObjectRemoved);

    // Удаляем новые обработчики
    this.canvas.off('mouse:down', this.boundHandlers.handleMouseDown);
    this.canvas.off('object:moving', this.boundHandlers.handleObjectMoving);
    this.canvas.off('mouse:up', this.boundHandlers.handleMouseUp);

    // Удаляем обработчики ActiveSelection
    this.canvas.off('selection:created', this.boundHandlers.handleSelectionCreated);
    this.canvas.off('selection:updated', this.boundHandlers.handleSelectionUpdated);
    this.canvas.off('selection:cleared', this.boundHandlers.handleSelectionCleared);
  }

  /**
   * Обработчик изменений в TextEntity
   */
  private handleEntityChanged(event: {
    entityId: string;
    metaBulletId: string;
    eventType: string;
    data?: unknown;
  }): void {
    const { entityId, metaBulletId, eventType, data } = event;

    // ВАЖНО: Игнорируем события от сущности которая находится за границами во время drag'а
    // Это предотвращает ненужную рекалькуляцию старого метабуллита
    if (
      this.draggedEntity &&
      this.draggedEntity.id === entityId &&
      this.isDragOutOfBounds &&
      eventType === 'entity_modified'
    ) {
      // console.debug('🚫 Ignoring entity_modified event for dragged entity outside bounds:', {
      //   entityId,
      //   isDragOutOfBounds: this.isDragOutOfBounds,
      // });
      return;
    }

    const metaBullet = this.metaBullets.get(metaBulletId);
    if (!metaBullet) {
      console.warn('⚠️ MetaBullet not found for entity event:', {
        metaBulletId,
        systemId: this.canvasId,
        metaBulletsCount: this.metaBullets.size,
        availableMetaBullets: Array.from(this.metaBullets.keys()),
        canvasSystemReference: this.canvas._metaBulletSystem === this ? 'SAME' : 'DIFFERENT',
      });
      return;
    }

    // console.debug(eventType, ' eventType !!!!!!!!!!!!!!!!!!!');

    // Обрабатываем разные типы событий
    switch (eventType) {
      case 'text_changed':
      case 'type_changed':
      case 'size_changed': {
        // 🎯 КРИТИЧНО: Проверяем флаг _isReplacingMarker
        // Если идет замена маркера, НЕ создаем бекап и НЕ делаем рекалькуляцию здесь
        // (всё будет сделано после создания нового маркера)
        const entity = this.canvas.getObjects().find((obj) => (obj as any).id === entityId) as
          | TextEntity
          | undefined;
        const isReplacingMarker = entity && (entity as any)._isReplacingMarker;

        // 🎯 КРИТИЧНО: Пропускаем рекалькуляцию если идет замена маркера!
        // Рекалькуляция будет вызвана после создания нового маркера в handleTabPress/handleShiftTabPress
        if (isReplacingMarker && eventType === 'type_changed') {
          console.debug('⏭️ [handleEntityChanged] Skipping recalc - marker is being replaced');
          break;
        }

        // ✅ Создаем бэкап перед изменением типа (только если НЕ идет замена маркера)
        if (this.backupManager && eventType === 'type_changed' && !isReplacingMarker) {
          this.backupManager.startOperation('style_change', [entityId]);
        }

        // Пересчитываем позиции при изменении текста или типа
        console.debug('🔄 [handleEntityChanged] recalculatePositions for:', eventType);
        metaBullet.recalculatePositions(true);

        // ✅ Завершаем операцию бэкапа (только если НЕ идет замена маркера)
        if (this.backupManager && eventType === 'type_changed' && !isReplacingMarker) {
          this.backupManager.requestBackup();
          this.backupManager.finishOperation();
        }
        break;
      }

      case 'level_changed':
        // При изменении уровня буллита тоже пересчитываем
        metaBullet.recalculatePositions(true);
        break;

      case 'position_changed': {
        // 🎯 ИСПРАВЛЕНИЕ: Если первая сущность - bullet и идет drag - не пересчитываем
        const entities = metaBullet.getEntities();
        if (entities.length > 0 && entities[0].id === entityId) {
          const firstEntity = entities[0];
          if (
            firstEntity.entityType.startsWith('bullet-level-') &&
            this.draggedEntity?.id === entityId
          ) {
            break;
          }
        }

        // При перемещении объекта пересчитываем позиции всего метабуллита
        // console.debug('🔄 Recalculating MetaBullet positions due to drag & drop:', {
        //   metaBulletId,
        //   entityId,
        //   newPosition: data,
        // });
        metaBullet.recalculatePositions(true);
        break;
      }

      case 'entity_modified': {
        // Объединенное событие - любые изменения в сущности (текст, позиция, размеры)
        // Блокируем обработку для новых сущностей (созданных через Enter, но еще не заполненных)
        // Бекап будет создан только при выходе из редактирования, если текст был изменен
        const entity = this.canvas.getObjects().find((obj) => (obj as any).id === entityId) as
          | TextEntity
          | undefined;
        if (entity && (entity as any)._isNewlyCreated) {
          break;
        }

        // 🎯 ИСПРАВЛЕНИЕ: Если первая сущность - bullet и идет drag - пропускаем
        const entitiesForModified = metaBullet.getEntities();
        if (entitiesForModified.length > 0 && entitiesForModified[0].id === entityId) {
          const firstEntityForModified = entitiesForModified[0];
          if (
            firstEntityForModified.entityType.startsWith('bullet-level-') &&
            this.draggedEntity?.id === entityId
          ) {
            break;
          }
        }

        //  Если сущность перетаскивается в другой MetaBullet - пропускаем
        // Это предотвращает визуальную анимацию "возврата" в старый MetaBullet
        if (
          this.draggedEntity &&
          this.draggedEntity.id === entityId &&
          this.isOverTargetMetaBullet
        ) {
          break;
        }

        // 🎯 ИСПРАВЛЕНИЕ: Если это перетаскивание сущности, пересортируем по Y-координате
        if (this.draggedEntity && this.draggedEntity.id === entityId) {
          this.reorderEntitiesInMetaBullet(metaBullet, this.draggedEntity);
        } else {
          // Обычная рекалькуляция без пересортировки
          metaBullet.recalculatePositions(true);
        }
        break;
      }
    }
  }

  /**
   * Пересортировать сущности в метабуллите по Y-координате при перетаскивании
   */
  private reorderEntitiesInMetaBullet(metaBullet: MetaBullet, draggedEntity: TextEntity): void {
    const entities = metaBullet.getEntities();
    if (entities.length <= 1) {
      return;
    }

    // Сортируем сущности по Y-координате (top)
    const sortedEntities = entities.sort((a, b) => (a.top || 0) - (b.top || 0));

    // Обновляем массив entityIds в метабуллите
    metaBullet.entityIds = sortedEntities.map((entity) => entity.id);

    // Обновляем entityOrder для каждой сущности
    sortedEntities.forEach((entity, index) => {
      entity.entityOrder = index;
    });

    // Пересчитываем позиции согласно новому порядку
    metaBullet.recalculatePositions(true);
  }

  /**
   * Обработчик удаления объектов с canvas
   *
   * Этот метод автоматически очищает MetaBullet-ы когда их TextEntity удаляются.
   * ВАЖНО: Имеет защиту от ложных срабатываний во время технических операций.
   */
  private handleObjectRemoved(event: { target: unknown }): void {
    // ⚠️ ЗАЩИТА ОТ ЛОЖНЫХ СРАБАТЫВАНИЙ:
    // Игнорируем события удаления объектов если система настроена на игнорирование.
    // Это критично во время массовых операций типа canvas.clear() в reorderCanvasObjects(),
    // когда объекты технически удаляются и добавляются обратно, но MetaBullet-ы
    // должны остаться нетронутыми.
    if (this.isIgnoringObjectRemovals) {
      return;
    }

    const removedObject = event.target;

    // ВАЖНО: Дополнительная проверка на ActiveSelection
    if (TextEntity.isTextEntity(removedObject)) {
      // Проверяем, находится ли объект все еще на canvas
      const canvasObjects = this.canvas.getObjects();
      const stillOnCanvas = canvasObjects.some((obj: any) => obj.id === removedObject.id);

      if (stillOnCanvas) {
        return;
      }

      // Дополнительная проверка: не находимся ли мы в процессе очистки ActiveSelection
      if (this.activeSelectionEntities.has(removedObject.id)) {
        return;
      }

      // Проверяем, есть ли активное выделение (может быть в процессе изменения)
      const activeSelection = this.canvas.getActiveObject();
      if (activeSelection && (activeSelection as any).type === 'activeSelection') {
        const activeSelectionObjects = (activeSelection as any)._objects || [];
        const isInActiveSelection = activeSelectionObjects.some(
          (obj: any) => obj.id === removedObject.id,
        );

        if (isInActiveSelection) {
          return;
        }
      }

      // Проверяем, что у объекта есть metaBulletId
      if (removedObject.metaBulletId) {
        // Маркеры должны быть удалены в _deleteEmptyEntityAndFocusPrevious или safeDelete
        // Здесь не удаляем, так как this.canvas уже undefined

        const metaBullet = this.metaBullets.get(removedObject.metaBulletId);
        if (metaBullet) {
          metaBullet.removeEntity(removedObject.id);

          // Если метабуллит стал пустым, удаляем его
          if (metaBullet.entityIds.length === 0) {
            this.metaBullets.delete(removedObject.metaBulletId);
          }
        } else {
          console.warn('⚠️ MetaBullet not found for removed entity:', {
            entityId: removedObject.id,
            metaBulletId: removedObject.metaBulletId,
            systemId: this.canvasId,
            availableMetaBullets: Array.from(this.metaBullets.keys()),
          });
        }
      }
    }
  }

  /**
   * Обработчик начала перетаскивания
   */
  private handleMouseDown(event: { target?: unknown }): void {
    const target = event.target;

    if (TextEntity.isTextEntity(target) && target.metaBulletId) {
      const metaBullet = this.metaBullets.get(target.metaBulletId);
      if (!metaBullet) return;

      const entities = metaBullet.getEntities();
      const isFirstEntity = entities.length > 0 && entities[0].id === target.id;

      // 🎯 ПРОВЕРКА: Если это первая сущность И это буллит - не двигаем весь метабулит
      if (isFirstEntity && target.entityType.startsWith('bullet-level-')) {
        // Разрешаем обычный drag с snap back
        this.draggedEntity = target;
        this.draggedEntityOriginalMetaBullet = metaBullet;
        this.isDragOutOfBounds = false;

        // Сохраняем начальную позицию для сравнения
        this.draggedEntityOriginalPosition = {
          left: target.left || 0,
          top: target.top || 0,
        };
        this.hasActuallyMoved = false;

        // 🎯 КРИТИЧНО: Сохраняем оригинальные границы метабуллита ДО начала драга
        this.originalMetaBulletBounds = this.calculateMetaBulletBounds(metaBullet);

        // Блокируем бекапы сразу при начале drag операции
        this.isProcessingGlobalOperation = true;

        // ✅ Начинаем операцию перетаскивания в BackupManager
        if (this.backupManager) {
          this.backupManager.startOperation('entity_drag', [target.id]);
        }
        return;
      }

      // Для остальных типов (title, heading, paragraph) - обычная логика
      this.draggedEntity = target;
      this.draggedEntityOriginalMetaBullet = metaBullet;
      this.isDragOutOfBounds = false;

      // Сохраняем начальную позицию для сравнения
      this.draggedEntityOriginalPosition = {
        left: target.left || 0,
        top: target.top || 0,
      };
      this.hasActuallyMoved = false;

      // 🎯 КРИТИЧНО: Сохраняем оригинальные границы метабуллита ДО начала драга
      this.originalMetaBulletBounds = this.calculateMetaBulletBounds(metaBullet);

      // Блокируем бекапы сразу при начале drag операции
      this.isProcessingGlobalOperation = true;

      // ✅ Начинаем операцию перетаскивания в BackupManager
      if (this.backupManager) {
        this.backupManager.startOperation('entity_drag', [target.id]);
      }
    }
  }

  /**
   * Обработчик перемещения объекта
   */
  private handleObjectMoving(event: { target?: unknown }): void {
    const target = event.target;

    // 🔢 ОБРАБАТЫВАЕМ связанное перетаскивание digital-accent
    if (TextEntity.isTextEntity(target) && target.isDigitalAccent && target.isDigitalAccent()) {
      // Обновляем позицию связанной сущности
      const linkedEntity = target.getLinkedAccentEntity();
      if (linkedEntity) {
        if (target.isDigitalAccentDigit()) {
          // Если перетаскиваем digit - обновляем позицию superscript
          target.updateLinkedAccentPosition();
        } else if (target.isDigitalAccentSuperscript()) {
          // Если перетаскиваем superscript - обновляем позицию digit
          this.updateDigitPositionFromSuperscript(target, linkedEntity);
        }
      } else {
        console.warn('🔢 No linked entity found for digital-accent:', {
          entityId: target.id,
          linkedAccentEntityId: target.linkedAccentEntityId,
        });
      }
    }

    // Обрабатываем перетаскивание ActiveSelection
    if (target && (target as any).type.toLowerCase() === 'activeselection') {
      this.isActiveSelectionMoving = true;

      this.updateMarkersForActiveSelection();
      return;
    }

    if (!TextEntity.isTextEntity(target) || !this.draggedEntity || target !== this.draggedEntity) {
      return;
    }

    if (!this.draggedEntityOriginalMetaBullet) {
      return;
    }

    // Отслеживаем реальное движение объекта
    if (
      this.draggedEntityOriginalPosition &&
      !this.hasActuallyMoved &&
      (Math.abs((target.left || 0) - this.draggedEntityOriginalPosition.left) > 1 ||
        Math.abs((target.top || 0) - this.draggedEntityOriginalPosition.top) > 1)
    ) {
      this.hasActuallyMoved = true;
    }

    // Проверяем расстояние от оригинального метабуллита
    const distance = this.calculateDistanceFromMetaBullet(
      target,
      this.draggedEntityOriginalMetaBullet,
    );

    // КРИТИЧНО: Используем гистерезис для предотвращения мерцания
    // Разные пороги для "выхода" и "возврата" обратно
    const wasOutOfBounds = this.isDragOutOfBounds;
    let isNowOutOfBounds: boolean;

    if (wasOutOfBounds) {
      // Если уже за границами - используем меньший порог для возврата (гистерезис)
      isNowOutOfBounds = distance > this.dragThresholdIn;
    } else {
      // Если внутри - используем больший порог для выхода
      isNowOutOfBounds = distance > this.dragThresholdOut;
    }

    // Обновляем состояние ДО показа/скрытия рамок
    this.isDragOutOfBounds = isNowOutOfBounds;

    // Логика показа рамки оригинального метабуллита
    if (isNowOutOfBounds && !wasOutOfBounds) {
      // Показываем границы метабуллита
      this.showMetaBulletBoundary(this.draggedEntityOriginalMetaBullet);
    } else if (!isNowOutOfBounds && wasOutOfBounds) {
      // Скрываем границы метабуллита
      this.hideMetaBulletBoundary();
    }

    // Проверяем возможность объединения с другими метабуллитами
    this.checkForMetaBulletMerge(target);
  }

  /**
   * Обработчик завершения перетаскивания
   */
  private async handleMouseUp(): Promise<void> {
    // Обрабатываем завершение перетаскивания ActiveSelection
    if (this.isActiveSelectionMoving) {
      this.isActiveSelectionMoving = false;
      return;
    }

    if (!this.draggedEntity || !this.draggedEntityOriginalMetaBullet) {
      return;
    }

    // console.debug('🖱️ Finished dragging TextEntity:', {
    //   entityId: this.draggedEntity.id,
    //   isDragOutOfBounds: this.isDragOutOfBounds,
    //   isOverTargetMetaBullet: this.isOverTargetMetaBullet,
    // });

    let hasChanges = false;

    // Если сущность находится над другим метабуллитом, объединяем их
    if (this.isOverTargetMetaBullet && this.targetMetaBullet) {
      this.mergeEntityIntoMetaBullet(
        this.draggedEntity,
        this.draggedEntityOriginalMetaBullet,
        this.targetMetaBullet,
      );
      hasChanges = true;
    }
    // Если сущность была вытащена за границы, создаем новый метабуллет
    else if (this.isDragOutOfBounds) {
      this.createNewMetaBulletFromDraggedEntity(
        this.draggedEntity,
        this.draggedEntityOriginalMetaBullet,
      );
      hasChanges = true;
    } else {
      // Обычное перемещение внутри метабуллита
      // Проверяем был ли реальный drag (не просто клик)
      if (this.hasActuallyMoved) {
        // ИСПРАВЛЕНО: Проверяем является ли перетаскиваемая сущность первой в метабуллите
        const metaBulletEntities = this.draggedEntityOriginalMetaBullet.getEntities();
        const isFirstEntity =
          metaBulletEntities.length > 0 && metaBulletEntities[0].id === this.draggedEntity.id;

        if (isFirstEntity) {
          // ИСПРАВЛЕНО: Если это первая сущность - ее движение должно двигать весь метабуллит
          // Поэтому НЕ делаем recalculate (не snap back), просто проверяем изменение позиции
          const finalPosition = {
            left: this.draggedEntity.left || 0,
            top: this.draggedEntity.top || 0,
          };

          const hasPositionChanged =
            Math.abs(finalPosition.left - this.draggedEntityOriginalPosition!.left) > 1 ||
            Math.abs(finalPosition.top - this.draggedEntityOriginalPosition!.top) > 1;

          if (hasPositionChanged) {
            hasChanges = true;
            // Пересчитываем позиции остальных сущностей относительно новой позиции первой
            await this.draggedEntityOriginalMetaBullet.recalculatePositions(true);
          }
        } else {
          // Для НЕ-первых сущностей - делаем snap back через recalculate
          await this.draggedEntityOriginalMetaBullet.recalculatePositions(true);

          // 🎯 КРИТИЧЕСКИ ВАЖНО: Проверяем финальную позицию ПОСЛЕ recalculate
          // Объект мог вернуться на исходную позицию через snap back
          const finalPosition = {
            left: this.draggedEntity?.left || 0,
            top: this.draggedEntity?.top || 0,
          };

          const hasPositionChanged =
            Math.abs(finalPosition.left - this.draggedEntityOriginalPosition!.left) > 1 ||
            Math.abs(finalPosition.top - this.draggedEntityOriginalPosition!.top) > 1;

          if (hasPositionChanged) {
            hasChanges = true;
          }
        }
      }
    }

    // ✅ Завершаем операцию перетаскивания в BackupManager
    if (this.backupManager) {
      if (hasChanges) {
        // Используем глобальную координацию бекапов
        this.scheduleGlobalBackup('entity_drag');
      }
      this.backupManager.finishOperation();
    }

    // Скрываем все границы и очищаем состояние
    this.hideMetaBulletBoundary();
    this.hideTargetMetaBulletBoundary();
    this.resetDragState();
  }

  /**
   * Проверить возможность объединения с другими метабуллитами
   */
  private checkForMetaBulletMerge(entity: TextEntity): void {
    // Ищем метабуллит в который может "заехать" перетаскиваемая сущность
    const potentialTargetMetaBullet = this.findMetaBulletUnderEntity(entity);

    // Если нашли новый целевой метабуллит
    if (
      potentialTargetMetaBullet &&
      potentialTargetMetaBullet.id !== this.draggedEntityOriginalMetaBullet?.id
    ) {
      if (
        !this.isOverTargetMetaBullet ||
        this.targetMetaBullet?.id !== potentialTargetMetaBullet.id
      ) {
        // Скрываем предыдущую границу целевого метабуллита
        this.hideTargetMetaBulletBoundary();

        // Показываем новую границу
        this.targetMetaBullet = potentialTargetMetaBullet;
        this.showTargetMetaBulletBoundary(potentialTargetMetaBullet);
        this.isOverTargetMetaBullet = true;

        // console.debug('🎯 Entity over target MetaBullet:', {
        //   entityId: entity.id,
        //   targetMetaBulletId: potentialTargetMetaBullet.id,
        // });
      }
    }
    // Если сущность больше не над целевым метабуллитом
    else if (this.isOverTargetMetaBullet && !potentialTargetMetaBullet) {
      this.hideTargetMetaBulletBoundary();
      this.targetMetaBullet = null;
      this.isOverTargetMetaBullet = false;

      // console.debug('❌ Entity left target MetaBullet area');
    }
  }

  /**
   * Найти метабуллит под перетаскиваемой сущностью
   */
  private findMetaBulletUnderEntity(entity: TextEntity): MetaBullet | null {
    const entityCenterX = (entity.left || 0) + (entity.width || 0) / 2;
    const entityCenterY = (entity.top || 0) + (entity.height || 0) / 2;

    // Проверяем все метабуллиты кроме оригинального
    for (const metaBullet of this.metaBullets.values()) {
      if (metaBullet.id === this.draggedEntityOriginalMetaBullet?.id) {
        continue; // Пропускаем оригинальный метабуллит
      }

      // Получаем границы метабуллита
      const entities = metaBullet.getEntities();
      if (entities.length === 0) continue;

      let minX = Infinity,
        minY = Infinity,
        maxX = -Infinity,
        maxY = -Infinity;

      entities.forEach((e) => {
        const left = e.left || 0;
        const top = e.top || 0;
        const right = left + (e.width || 0);
        const bottom = top + (e.height || 0);

        minX = Math.min(minX, left);
        minY = Math.min(minY, top);
        maxX = Math.max(maxX, right);
        maxY = Math.max(maxY, bottom);
      });

      // Добавляем отступ для более удобного "заезжания"
      const padding = 60;
      minX -= padding;
      minY -= padding;
      maxX += padding;
      maxY += padding;

      // Проверяем попадает ли центр сущности в область метабуллита
      if (
        entityCenterX >= minX &&
        entityCenterX <= maxX &&
        entityCenterY >= minY &&
        entityCenterY <= maxY
      ) {
        return metaBullet;
      }
    }

    return null;
  }

  /**
   * Показать границы целевого метабуллита (зеленые)
   */
  private showTargetMetaBulletBoundary(metaBullet: MetaBullet): void {
    // Сначала скрываем предыдущую границу если есть
    this.hideTargetMetaBulletBoundary();

    const entities = metaBullet.getEntities();

    if (entities.length === 0) {
      return;
    }

    // Вычисляем границы метабуллита
    let minX = Infinity,
      minY = Infinity,
      maxX = -Infinity,
      maxY = -Infinity;

    entities.forEach((entity) => {
      const left = entity.left || 0;
      const top = entity.top || 0;
      const right = left + (entity.width || 0);
      const bottom = top + (entity.height || 0);

      // Учитываем размеры TextEntity
      minX = Math.min(minX, left);
      minY = Math.min(minY, top);
      maxX = Math.max(maxX, right);
      maxY = Math.max(maxY, bottom);

      // 🎯 ИСПРАВЛЕНИЕ: Также учитываем размеры всех связанных маркеров для зеленой обводки
      const linkedMarkers = this.getLinkedMarkers(entity);
      linkedMarkers.forEach((marker) => {
        const markerLeft = marker.left || 0;
        const markerTop = marker.top || 0;
        const markerRight = markerLeft + (marker.width || 0);
        const markerBottom = markerTop + (marker.height || 0);

        minX = Math.min(minX, markerLeft);
        minY = Math.min(minY, markerTop);
        maxX = Math.max(maxX, markerRight);
        maxY = Math.max(maxY, markerBottom);
      });
    });

    // Добавляем отступы
    const padding = 60;
    minX -= padding;
    minY -= padding;
    maxX += padding;
    maxY += padding;

    // Создаем четыре линии для рамки (зеленые)
    const strokeOptions = {
      stroke: '#28a745', // Зеленый цвет для целевого метабуллита
      strokeWidth: 2,
      strokeDashArray: [8, 4], // Другой паттерн пунктира
      selectable: false,
      evented: false,
      excludeFromBackup: true, //Исключаем placeholder из бекапов
    };

    const topLine = new Line([minX, minY, maxX, minY], strokeOptions);
    const rightLine = new Line([maxX, minY, maxX, maxY], strokeOptions);
    const bottomLine = new Line([maxX, maxY, minX, maxY], strokeOptions);
    const leftLine = new Line([minX, maxY, minX, minY], strokeOptions);

    // Создаем группу из линий
    this.targetMetaBulletBoundaryRect = new Group([topLine, rightLine, bottomLine, leftLine], {
      selectable: false,
      evented: false,
      excludeFromExport: true,
      excludeFromBackup: true, // 🔄 ИСПРАВЛЕНИЕ: Исключаем placeholder из бекапов
    });

    // Добавляем на canvas
    this.canvas.add(this.targetMetaBulletBoundaryRect);
    this.canvas.requestRenderAll();

    // console.debug('🟢 Target MetaBullet boundary shown:', {
    //   metaBulletId: metaBullet.id,
    //   bounds: { minX, minY, maxX, maxY },
    // });
  }

  /**
   * Скрыть границы целевого метабуллита
   */
  private hideTargetMetaBulletBoundary(): void {
    if (this.targetMetaBulletBoundaryRect) {
      this.canvas.remove(this.targetMetaBulletBoundaryRect);
      this.targetMetaBulletBoundaryRect = null;
      this.canvas.requestRenderAll();
    }
  }

  /**
   * Объединить сущность с целевым метабуллитом
   */
  private mergeEntityIntoMetaBullet(
    entity: TextEntity,
    originalMetaBullet: MetaBullet,
    targetMetaBullet: MetaBullet,
  ): void {
    console.log('🤝 Merging entity into target MetaBullet:', {
      entityId: entity.id,
      entityType: entity.entityType,
      originalMetaBulletId: originalMetaBullet.id,
      targetMetaBulletId: targetMetaBullet.id,
      entityPosition: { left: entity.left, top: entity.top },
      isDigitalAccent: entity.isDigitalAccent?.(),
    });

    // 🔢 СПЕЦИАЛЬНАЯ ОБРАБОТКА ДЛЯ DIGITAL-ACCENT
    // Если сливается часть digital-accent, сливаем обе части вместе
    const entitiesToMerge: TextEntity[] = [entity];

    if (entity.isDigitalAccent && entity.isDigitalAccent()) {
      const linkedEntity = entity.getLinkedAccentEntity();
      if (linkedEntity) {
        console.log('🔢 Merging linked digital-accent entity together:', {
          draggedEntityId: entity.id,
          draggedEntityType: entity.entityType,
          linkedEntityId: linkedEntity.id,
          linkedEntityType: linkedEntity.entityType,
        });
        entitiesToMerge.push(linkedEntity);
      }
    }

    // ВАЖНО: Сначала удаляем все сущности из оригинального метабуллита
    entitiesToMerge.forEach((entityToMerge) => {
      originalMetaBullet.removeEntity(entityToMerge.id);
      // Временно сбрасываем связь с оригинальным метабуллитом
      entityToMerge.metaBulletId = '';
      entityToMerge.entityOrder = 0;
    });

    // 🎯 ИСПРАВЛЕНИЕ: Определяем правильную позицию вставки по Y-координате
    const insertIndex = this.calculateInsertPosition(entity, targetMetaBullet);

    console.log('🎯 Calculated insert position:', {
      entityId: entity.id,
      entityY: entity.top,
      insertIndex,
      targetMetaBulletEntities: targetMetaBullet.entityIds.length,
    });

    // 🎯 КРИТИЧНО: Определяем правильный baseX целевого метабулита
    // Если в целевом метабулите уже есть сущности, используем baseX первой из них
    // Иначе сохраняем текущую позицию мерджируемой сущности
    const targetEntities = targetMetaBullet.getEntities();
    let targetBaseX: number;

    if (targetEntities.length > 0) {
      // Используем baseX существующих сущностей целевого метабулита
      const firstTargetEntity = targetEntities[0];
      const isFirstBullet =
        firstTargetEntity.entityType &&
        (firstTargetEntity.entityType.startsWith('bullet-') ||
          firstTargetEntity.entityType === 'digital-accent');
      targetBaseX = isFirstBullet
        ? (firstTargetEntity.left || 0) - (firstTargetEntity.marginLeft || 0)
        : firstTargetEntity.left || 0;

      console.log('🎯 Target MetaBullet baseX from existing entities:', {
        targetMetaBulletId: targetMetaBullet.id,
        firstEntityId: firstTargetEntity.id,
        firstEntityType: firstTargetEntity.entityType,
        firstEntityLeft: firstTargetEntity.left,
        firstEntityMarginLeft: firstTargetEntity.marginLeft,
        isFirstBullet,
        targetBaseX,
      });
    } else {
      // Целевой метабулит пустой - сохраняем текущую X позицию первой мерджируемой сущности
      const firstMergedEntity = entitiesToMerge[0];
      const isFirstBullet =
        firstMergedEntity.entityType &&
        (firstMergedEntity.entityType.startsWith('bullet-') ||
          firstMergedEntity.entityType === 'digital-accent');
      targetBaseX = isFirstBullet
        ? (firstMergedEntity.left || 0) - (firstMergedEntity.marginLeft || 0)
        : firstMergedEntity.left || 0;

      console.log('🎯 Target MetaBullet baseX from merged entity (empty target):', {
        targetMetaBulletId: targetMetaBullet.id,
        firstMergedEntityId: firstMergedEntity.id,
        firstMergedEntityType: firstMergedEntity.entityType,
        firstMergedEntityLeft: firstMergedEntity.left,
        firstMergedEntityMarginLeft: firstMergedEntity.marginLeft,
        isFirstBullet,
        targetBaseX,
      });
    }

    // 🎯 КРИТИЧНО: Корректируем X-координату всех мерджируемых сущностей на baseX целевого метабулита
    entitiesToMerge.forEach((entityToMerge) => {
      const oldLeft = entityToMerge.left || 0;
      const isBullet =
        entityToMerge.entityType &&
        (entityToMerge.entityType.startsWith('bullet-') ||
          entityToMerge.entityType === 'digital-accent');
      const newLeft = isBullet ? targetBaseX + (entityToMerge.marginLeft || 0) : targetBaseX;

      console.log('🎯 Correcting entity X position for merge:', {
        entityId: entityToMerge.id,
        entityType: entityToMerge.entityType,
        isBullet,
        oldLeft,
        newLeft,
        targetBaseX,
        marginLeft: entityToMerge.marginLeft,
      });

      entityToMerge.set({ left: newLeft });
      entityToMerge.setCoords();
    });

    // Добавляем все сущности в целевой метабуллет в правильную позицию
    entitiesToMerge.forEach((entityToMerge, index) => {
      targetMetaBullet.addEntity(entityToMerge, insertIndex + index);
    });

    console.log('✅ Entities merged into target MetaBullet:', {
      entitiesCount: entitiesToMerge.length,
      entityIds: entitiesToMerge.map((e) => e.id),
      newMetaBulletId: targetMetaBullet.id,
      insertIndex,
    });

    // Если оригинальный метабуллет стал пустым, удаляем его
    if (originalMetaBullet.entityIds.length === 0) {
      this.metaBullets.delete(originalMetaBullet.id);
      console.log('🗑️ Original MetaBullet removed (empty):', originalMetaBullet.id);
    }

    // 🔄 ИСПРАВЛЕНИЕ: Не отправляем немедленный бекап, координация будет в handleMouseUp
  }

  /**
   * Вычислить правильную позицию вставки сущности в метабуллит по Y-координате
   */
  private calculateInsertPosition(entity: TextEntity, targetMetaBullet: MetaBullet): number {
    const entities = targetMetaBullet.getEntities();

    if (entities.length === 0) {
      return 0; // Первая сущность
    }

    const entityY = entity.top || 0;
    const entityCenterY = entityY + (entity.height || 0) / 2;

    // Находим позицию где Y-координата перетаскиваемой сущности должна быть вставлена
    for (let i = 0; i < entities.length; i++) {
      const existingEntity = entities[i];
      const existingCenterY = (existingEntity.top || 0) + (existingEntity.height || 0) / 2;

      // Если центр перетаскиваемой сущности выше центра текущей сущности
      if (entityCenterY < existingCenterY) {
        return i;
      }
    }

    // Если не нашли подходящую позицию, вставляем в конец
    return entities.length;
  }

  /**
   * Рассчитать границы метабуллита включая все сущности и их маркеры
   */
  private calculateMetaBulletBounds(
    metaBullet: MetaBullet,
    excludeEntityId?: string,
  ): {
    minX: number;
    minY: number;
    maxX: number;
    maxY: number;
  } {
    const entities = metaBullet.getEntities().filter((e) => e.id !== excludeEntityId);

    if (entities.length === 0) {
      return { minX: 0, minY: 0, maxX: 0, maxY: 0 };
    }

    // Находим границы всех сущностей метабуллита
    let minX = Infinity,
      minY = Infinity,
      maxX = -Infinity,
      maxY = -Infinity;

    entities.forEach((e) => {
      const left = e.left || 0;
      const top = e.top || 0;
      const right = left + (e.width || 0);
      const bottom = top + (e.height || 0);

      // Учитываем размеры TextEntity
      minX = Math.min(minX, left);
      minY = Math.min(minY, top);
      maxX = Math.max(maxX, right);
      maxY = Math.max(maxY, bottom);

      // Также учитываем размеры маркеров для расчета границ
      const linkedMarkers = this.getLinkedMarkers(e);
      linkedMarkers.forEach((marker) => {
        const markerLeft = marker.left || 0;
        const markerTop = marker.top || 0;
        const markerRight = markerLeft + (marker.width || 0);
        const markerBottom = markerTop + (marker.height || 0);

        minX = Math.min(minX, markerLeft);
        minY = Math.min(minY, markerTop);
        maxX = Math.max(maxX, markerRight);
        maxY = Math.max(maxY, markerBottom);
      });
    });

    return { minX, minY, maxX, maxY };
  }

  /**
   * Вычислить расстояние от сущности до границ её метабуллита
   */
  private calculateDistanceFromMetaBullet(entity: TextEntity, metaBullet: MetaBullet): number {
    // 🎯 КРИТИЧНО: Используем сохраненные оригинальные границы вместо пересчета
    if (!this.originalMetaBulletBounds) {
      console.warn('⚠️ No original MetaBullet bounds saved, falling back to current calculation');
      // Fallback к старой логике если границы не сохранены
      const bounds = this.calculateMetaBulletBounds(metaBullet, entity.id);
      return this.calculateDistanceFromBounds(entity, bounds);
    }

    return this.calculateDistanceFromBounds(entity, this.originalMetaBulletBounds);
  }

  /**
   * Рассчитать расстояние от сущности до заданных границ
   */
  private calculateDistanceFromBounds(
    entity: TextEntity,
    bounds: {
      minX: number;
      minY: number;
      maxX: number;
      maxY: number;
    },
  ): number {
    const { minX, minY, maxX, maxY } = bounds;

    // 🎯 ИСПРАВЛЕНИЕ: Получаем эффективные границы перетаскиваемой сущности включая маркеры
    // Базовые границы сущности (без маркеров)
    const baseEntityLeft = entity.left || 0;
    const baseEntityTop = entity.top || 0;
    const baseEntityRight = baseEntityLeft + (entity.width || 0);
    const baseEntityBottom = baseEntityTop + (entity.height || 0);

    // Изначально границы равны размерам TextEntity
    let effectiveMinX = baseEntityLeft;
    let effectiveMinY = baseEntityTop;
    let effectiveMaxX = baseEntityRight;
    let effectiveMaxY = baseEntityBottom;

    // Учитываем маркеры перетаскиваемой сущности
    const draggedEntityMarkers = this.getLinkedMarkers(entity);
    draggedEntityMarkers.forEach((marker) => {
      const markerLeft = marker.left || 0;
      const markerTop = marker.top || 0;
      const markerRight = markerLeft + (marker.width || 0);
      const markerBottom = markerTop + (marker.height || 0);

      effectiveMinX = Math.min(effectiveMinX, markerLeft);
      effectiveMinY = Math.min(effectiveMinY, markerTop);
      effectiveMaxX = Math.max(effectiveMaxX, markerRight);
      effectiveMaxY = Math.max(effectiveMaxY, markerBottom);
    });

    // 🎯 ИСПРАВЛЕНИЕ: Проверяем пересечение областей вместо центра
    // Сущность считается "внутри" если её область пересекается с областью метабуллита
    const entityLeft = effectiveMinX;
    const entityTop = effectiveMinY;
    const entityRight = effectiveMaxX;
    const entityBottom = effectiveMaxY;

    // Проверяем пересечение прямоугольников
    const isIntersecting = !(
      entityRight < minX ||
      entityLeft > maxX ||
      entityBottom < minY ||
      entityTop > maxY
    );

    if (isIntersecting) {
      return 0; // Сущность пересекается с метабуллитом
    }

    // КРИТИЧНО: Вычисляем расстояние правильно для угловых случаев
    // Когда сущность за углом (например влево И вверх), нужно евклидово расстояние!
    let distanceX = 0;
    let distanceY = 0;

    // Рассчитываем расстояние по X
    if (entityRight < minX) {
      // Вышли влево от метабуллита
      distanceX = minX - entityRight;
    } else if (entityLeft > maxX) {
      // Вышли вправо от метабуллита
      distanceX = entityLeft - maxX;
    }
    // Если entityRight >= minX И entityLeft <= maxX, то distanceX = 0 (внутри по X)

    // Рассчитываем расстояние по Y
    if (entityBottom < minY) {
      // Вышли вверх от метабуллита
      distanceY = minY - entityBottom;
    } else if (entityTop > maxY) {
      // Вышли вниз от метабуллита
      distanceY = entityTop - maxY;
    }
    // Если entityBottom >= minY И entityTop <= maxY, то distanceY = 0 (внутри по Y)

    // Используем евклидово расстояние (гипотенуза)
    // Это решает проблему мерцания когда сущность за углом
    const distance = Math.sqrt(distanceX * distanceX + distanceY * distanceY);

    // Возвращаем евклидово расстояние
    return distance;
  }

  /**
   * Получить все маркеры связанные с TextEntity
   */
  private getLinkedMarkers(entity: TextEntity): any[] {
    const allObjects = this.canvas.getObjects();
    return allObjects.filter((obj) => {
      const marker = obj as unknown as any;
      return obj.type === 'marker' && !!marker.isMarker && marker.entityId === entity.id;
    });
  }

  /**
   * Показать границы метабуллита
   */
  private showMetaBulletBoundary(metaBullet: MetaBullet): void {
    // Сначала скрываем предыдущую границу если есть
    this.hideMetaBulletBoundary();

    // Скрываем hover подсветку когда появляется красная рамка
    if (this.currentHoveredMetaBulletId) {
      this.currentHoveredMetaBulletId = null;
      this.hideHoverHighlight();
    }

    const entities = metaBullet.getEntities().filter((e) => e.id !== this.draggedEntity?.id);

    if (entities.length === 0) {
      return;
    }

    // Вычисляем границы метабуллита
    let minX = Infinity,
      minY = Infinity,
      maxX = -Infinity,
      maxY = -Infinity;

    entities.forEach((entity) => {
      const left = entity.left || 0;
      const top = entity.top || 0;
      const right = left + (entity.width || 0);
      const bottom = top + (entity.height || 0);

      // Учитываем размеры TextEntity
      minX = Math.min(minX, left);
      minY = Math.min(minY, top);
      maxX = Math.max(maxX, right);
      maxY = Math.max(maxY, bottom);

      // 🎯 ИСПРАВЛЕНИЕ: Также учитываем размеры всех связанных маркеров
      const linkedMarkers = this.getLinkedMarkers(entity);
      linkedMarkers.forEach((marker) => {
        const markerLeft = marker.left || 0;
        const markerTop = marker.top || 0;
        const markerRight = markerLeft + (marker.width || 0);
        const markerBottom = markerTop + (marker.height || 0);

        minX = Math.min(minX, markerLeft);
        minY = Math.min(minY, markerTop);
        maxX = Math.max(maxX, markerRight);
        maxY = Math.max(maxY, markerBottom);
      });
    });

    // Добавляем отступы
    const padding = 60;
    minX -= padding;
    minY -= padding;
    maxX += padding;
    maxY += padding;

    // Создаем четыре линии для рамки
    const strokeOptions = {
      stroke: '#ff6b6b',
      strokeWidth: 2,
      strokeDashArray: [5, 5],
      selectable: false,
      evented: false,
      excludeFromBackup: true, // 🔄 ИСПРАВЛЕНИЕ: Исключаем placeholder из бекапов
    };

    const topLine = new Line([minX, minY, maxX, minY], strokeOptions);
    const rightLine = new Line([maxX, minY, maxX, maxY], strokeOptions);
    const bottomLine = new Line([maxX, maxY, minX, maxY], strokeOptions);
    const leftLine = new Line([minX, maxY, minX, minY], strokeOptions);

    // Создаем группу из линий
    this.metaBulletBoundaryRect = new Group([topLine, rightLine, bottomLine, leftLine], {
      selectable: false,
      evented: false,
      excludeFromExport: true,
      excludeFromBackup: true, // 🔄 ИСПРАВЛЕНИЕ: Исключаем placeholder из бекапов
    });

    // Добавляем на canvas
    this.canvas.add(this.metaBulletBoundaryRect);
    this.canvas.requestRenderAll();
  }

  /**
   * Скрыть границы метабуллита
   */
  private hideMetaBulletBoundary(): void {
    if (this.metaBulletBoundaryRect) {
      this.canvas.remove(this.metaBulletBoundaryRect);
      this.metaBulletBoundaryRect = null;
      this.canvas.requestRenderAll();
    }
  }

  /**
   * Создать новый метабуллет из вытащенной сущности
   */
  private createNewMetaBulletFromDraggedEntity(
    entity: TextEntity,
    originalMetaBullet: MetaBullet,
  ): void {
    console.log('🆕 Creating new MetaBullet from dragged entity:', {
      entityId: entity.id,
      entityType: entity.entityType,
      originalMetaBulletId: originalMetaBullet.id,
      isDigitalAccent: entity.isDigitalAccent?.(),
    });

    // 🔢 СПЕЦИАЛЬНАЯ ОБРАБОТКА ДЛЯ DIGITAL-ACCENT
    // Если перетаскивается часть digital-accent, переносим обе части вместе
    const entitiesToMove: TextEntity[] = [entity];

    if (entity.isDigitalAccent && entity.isDigitalAccent()) {
      const linkedEntity = entity.getLinkedAccentEntity();
      if (linkedEntity) {
        console.log('🔢 Moving linked digital-accent entity together:', {
          draggedEntityId: entity.id,
          draggedEntityType: entity.entityType,
          linkedEntityId: linkedEntity.id,
          linkedEntityType: linkedEntity.entityType,
        });
        entitiesToMove.push(linkedEntity);
      }
    }

    // ВАЖНО: Сначала удаляем все сущности из оригинального метабуллита
    entitiesToMove.forEach((entityToMove) => {
      originalMetaBullet.removeEntity(entityToMove.id);
      // Сбрасываем связь с оригинальным метабуллитом
      entityToMove.metaBulletId = '';
      entityToMove.entityOrder = 0;
    });

    // Теперь создаем новый метабуллет со всеми сущностями
    const newMetaBulletId = this.createMetaBullet(entitiesToMove);

    console.log('✅ New MetaBullet created from drag:', {
      newMetaBulletId,
      entityId: entity.id,
      originalMetaBulletId: originalMetaBullet.id,
    });

    // Если оригинальный метабуллет стал пустым, удаляем его
    if (originalMetaBullet.entityIds.length === 0) {
      this.metaBullets.delete(originalMetaBullet.id);
      console.log('🗑️ Original MetaBullet removed (empty):', originalMetaBullet.id);
    }

    // 🔄 ИСПРАВЛЕНИЕ: Не отправляем немедленный бекап, координация будет в handleMouseUp
  }

  /**
   * Запланировать бекап после смены типа/уровня сущности (публичный метод)
   * Используется в handleTabPress/handleShiftTabPress/changeEntityTypeWithMarker
   */
  public scheduleBackupAfterTypeChange(entityId: string): void {
    // Используем глобальную координацию бекапов с задержкой
    this.scheduleGlobalBackup('entity_type_change');
  }

  /**
   * Запланировать глобальный бекап после завершения всех операций
   */
  private scheduleGlobalBackup(reason: string): void {
    if (this.globalOperationBackupScheduled) {
      return;
    }

    this.globalOperationBackupScheduled = true;
    // isProcessingGlobalOperation уже должен быть true из handleMouseDown

    // Ожидаем завершения всех рекалькуляций с небольшой задержкой
    setTimeout(() => {
      this.executeGlobalBackup(reason);
    }, 100); // Даём время на завершение всех операций
  }

  /**
   * Выполнить глобальный бекап после завершения всех операций
   */
  private executeGlobalBackup(reason: string): void {
    if (!this.globalOperationBackupScheduled) {
      return; // Бекап уже выполнен или отменён
    }

    // Отправляем единое событие для создания бекапа
    this.canvas.fire('metaBullet:backup:needed', {
      reason: `global_${reason}`,
      metaBulletsCount: this.metaBullets.size,
      systemId: this.canvasId,
      timestamp: Date.now(),
    });

    // Сбрасываем только флаг планирования (isProcessingGlobalOperation сбросится в resetDragState)
    this.globalOperationBackupScheduled = false;
  }

  /**
   * Проверить, идёт ли глобальная операция (для блокировки промежуточных бекапов)
   */
  get isProcessingGlobalDragOperation(): boolean {
    return this.isProcessingGlobalOperation;
  }

  /**
   * Сбросить состояние перетаскивания
   */
  private resetDragState(): void {
    this.draggedEntity = null;
    this.draggedEntityOriginalMetaBullet = null;
    this.isDragOutOfBounds = false;

    // Сбрасываем флаги отслеживания движения
    this.draggedEntityOriginalPosition = null;
    this.hasActuallyMoved = false;

    // 🎯 КРИТИЧНО: Очищаем сохраненные оригинальные границы
    this.originalMetaBulletBounds = null;

    // 🔄 ИСПРАВЛЕНИЕ: Разблокируем бекапы при сбросе состояния
    this.isProcessingGlobalOperation = false;

    // Сбрасываем состояние объединения
    this.targetMetaBullet = null;
    this.isOverTargetMetaBullet = false;
  }

  /**
   * Показать подсветку MetaBullet при наведении мыши
   */
  public showHoverHighlight(metaBulletId: string): void {
    // 🎯 КРИТИЧЕСКИ ВАЖНО: НЕ показываем hover подсветку если есть красный placeholder (drag out of bounds)
    if (this.isDragOutOfBounds && this.metaBulletBoundaryRect) {
      return;
    }

    const metaBullet = this.metaBullets.get(metaBulletId);
    if (!metaBullet) {
      return;
    }

    // НЕ показываем hover подсветку если метабуллит уже в ActiveSelection
    const entities = metaBullet.getEntities();
    if (entities.length > 0) {
      const firstEntity = entities[0];
      const isInActiveSelection = firstEntity.group && firstEntity.group.type === 'activeselection';

      // console.debug('🎯 Checking if MetaBullet is in ActiveSelection:', {
      //   metaBulletId,
      //   firstEntityId: firstEntity.id,
      //   hasGroup: !!firstEntity.group,
      //   groupType: firstEntity.group?.type,
      //   isInActiveSelection,
      // });

      if (isInActiveSelection) {
        return;
      }
    }

    // Скрываем предыдущую подсветку если была
    this.hideHoverHighlight();

    // Hover подсветка может сосуществовать с editing подсветкой

    // Вычисляем границы MetaBullet включая все маркеры
    if (entities.length === 0) return;

    let minX = Infinity,
      minY = Infinity,
      maxX = -Infinity,
      maxY = -Infinity;

    entities.forEach((e) => {
      const left = e.left || 0;
      const top = e.top || 0;
      const right = left + (e.width || 0);
      const bottom = top + (e.height || 0);

      minX = Math.min(minX, left);
      minY = Math.min(minY, top);
      maxX = Math.max(maxX, right);
      maxY = Math.max(maxY, bottom);

      // Учитываем маркеры
      const linkedMarkers = this.getLinkedMarkers(e);
      linkedMarkers.forEach((marker) => {
        const markerLeft = marker.left || 0;
        const markerTop = marker.top || 0;
        const markerRight = markerLeft + (marker.width || 0);
        const markerBottom = markerTop + (marker.height || 0);

        minX = Math.min(minX, markerLeft);
        minY = Math.min(minY, markerTop);
        maxX = Math.max(maxX, markerRight);
        maxY = Math.max(maxY, markerBottom);
      });
    });

    // Добавляем небольшой padding
    const padding = 10;
    minX -= padding;
    minY -= padding;
    maxX += padding;
    maxY += padding;

    const width = maxX - minX;
    const height = maxY - minY;

    // console.debug('🎯 Showing hover highlight for MetaBullet:', {
    //   metaBulletId,
    //   bounds: { minX, minY, maxX, maxY, width, height },
    //   entitiesCount: entities.length,
    // });

    // Создаем подсветку для hover (отличается от editing)
    const highlightOptions = {
      left: minX,
      top: minY,
      width: width,
      height: height,
      fill: 'rgba(59, 130, 246, 0.06)', // Синяя заливка для hover (отличается от зеленой editing)
      stroke: 'rgba(59, 130, 246, 0.4)', // Синяя граница для hover
      strokeWidth: 1.5, // Такая же толщина как у editing
      strokeDashArray: [8, 4], // Другой паттерн пунктира - более длинные штрихи
      selectable: true, // Рамка селектабельна для выделения всего metaBullet
      evented: true, // Включаем события - но проверяем, есть ли под кликом другие объекты
      excludeFromExport: true,
      excludeFromBackup: true,
      hasControls: false,
      hasBorders: false,
      lockMovementX: true, // Сама рамка не двигается
      lockMovementY: true, // Сама рамка не двигается
      hoverCursor: 'move', // Курсор перемещения
      moveCursor: 'move',
      // Кастомные свойства для идентификации
      isMetaBulletFrame: true,
      metaBulletId: metaBulletId,
    };

    // Создаем прямоугольник подсветки для hover
    // console.debug('🎯 Creating hover highlight with bounds:', { minX, minY, width, height });
    this.hoverHighlightRect = new Rect(highlightOptions) as any;
    // console.debug('🎯 Hover highlight rect created successfully:', !!this.hoverHighlightRect);

    if (this.hoverHighlightRect) {
      // Добавляем обработчик клика для создания ActiveSelection
      this.hoverHighlightRect.on('mousedown', async (e) => {
        await this.selectMetaBulletAsGroup(metaBulletId);
        e.e.preventDefault();
        e.e.stopPropagation();
      });

      this.canvas.add(this.hoverHighlightRect);

      // Находим индекс самого первого объекта метабулита (текст или маркер)
      // и вставляем рамку перед ним, чтобы маркеры были выше highlight
      const allObjects = this.canvas.getObjects();
      let firstMetaBulletObjectIndex = -1;

      // Собираем все объекты метабулита (тексты и маркеры)
      const metaBulletObjectIds = new Set<string>();
      entities.forEach((entity) => {
        metaBulletObjectIds.add(entity.id);
        // Добавляем связанные маркеры
        const linkedMarkers = this.getLinkedMarkers(entity);
        linkedMarkers.forEach((marker) => {
          if (marker.id) {
            metaBulletObjectIds.add(marker.id);
          }
        });
      });

      // Ищем самый первый объект метабулита (самый нижний слой)
      for (let i = 0; i < allObjects.length; i++) {
        const obj = allObjects[i];
        const objId = (obj as any).id;
        if (objId && metaBulletObjectIds.has(objId)) {
          firstMetaBulletObjectIndex = i;
          break;
        }
      }

      // Если нашли объект метабулита, вставляем рамку перед ним
      if (firstMetaBulletObjectIndex >= 0) {
        this.canvas.remove(this.hoverHighlightRect);
        this.canvas.insertAt(firstMetaBulletObjectIndex, this.hoverHighlightRect);
      }
      // Если объект не найден, рамка остается в конце (fallback)

      this.canvas.requestRenderAll();
      // console.debug('✨ MetaBullet hover highlight added to canvas (after TextEntity)');
    } else {
      console.error('❌ Failed to create hover highlight rect!');
    }
  }

  /**
   * Скрыть подсветку MetaBullet при убирании мыши
   */
  public hideHoverHighlight(): void {
    if (this.hoverHighlightRect) {
      this.canvas.remove(this.hoverHighlightRect);
      this.hoverHighlightRect = null;
      this.canvas.requestRenderAll();
      // console.debug('✨ MetaBullet hover highlight hidden');
    }
  }

  /**
   * Выделить все объекты MetaBullet как группу (ActiveSelection)
   */
  public async selectMetaBulletAsGroup(metaBulletId: string): Promise<void> {
    const metaBullet = this.metaBullets.get(metaBulletId);
    if (!metaBullet) {
      return;
    }

    // Получаем все сущности MetaBullet
    const entities = metaBullet.getEntities();
    if (entities.length === 0) {
      return;
    }

    // === ДОБАВЛЯЕМ СВЯЗАННЫЕ DIGITAL-ACCENT СУЩНОСТИ ===
    const allEntities = [...entities];
    entities.forEach((entity) => {
      if (entity.isDigitalAccent && entity.isDigitalAccent()) {
        const linkedEntity = entity.getLinkedAccentEntity();
        if (linkedEntity && !allEntities.includes(linkedEntity)) {
          allEntities.push(linkedEntity);
        }
      }
    });

    // Получаем все уникальные маркеры MetaBullet
    const markersSet = new Set<any>();
    const markerContentObjects: any[] = [];

    allEntities.forEach((entity) => {
      const entityMarkers = this.getLinkedMarkers(entity);

      entityMarkers.forEach((marker) => {
        markersSet.add(marker);

        // ВКЛЮЧАЕМ контент маркеров в ActiveSelection!
        // При этом отключаем их автоматическое позиционирование
        const contentObjects = this.canvas
          .getObjects()
          .filter((obj: any) => obj.markerId === marker.id);

        markerContentObjects.push(...contentObjects);
      });
    });

    const markers = Array.from(markersSet);

    // Объединяем все объекты: TextEntity + маркеры + контент маркеров
    const allObjects = [...allEntities, ...markers, ...markerContentObjects];

    // Скрываем hover рамку перед созданием ActiveSelection
    this.hideHoverHighlight();

    // СНАЧАЛА СБРАСЫВАЕМ ВСЁ ВЫДЕЛЕНИЕ
    this.canvas.discardActiveObject();
    this.canvas.requestRenderAll();

    // ВКЛЮЧАЕМ режим ActiveSelection
    this.isActiveSelectionMode = true;

    // УСТАНАВЛИВАЕМ ФЛАГ перед программным созданием выделения (клик на placeholder)
    this.isProcessingSmartSelection = true;

    try {
      // Используем helper метод для создания ActiveSelection с правильным позиционированием
      const activeSelection = this.createActiveSelectionWithPositionFix(allObjects);

      // Устанавливаем ActiveSelection как активный объект
      this.canvas.setActiveObject(activeSelection);

      this.canvas.requestRenderAll();
    } finally {
      // СБРАСЫВАЕМ ФЛАГ после завершения программного выделения
      this.isProcessingSmartSelection = false;
    }
  }

  /**
   * Создать ActiveSelection с правильным позиционированием объектов
   * Предотвращает "улетание" объектов при создании группы
   */
  private createActiveSelectionWithPositionFix(objects: any[]): ActiveSelection {
    // 1. СОХРАНЯЕМ оригинальные позиции ПЕРЕД созданием ActiveSelection
    const originalPositions = new Map<string, { left: number; top: number }>();
    objects.forEach((obj) => {
      if (obj.id) {
        originalPositions.set(obj.id, { left: obj.left || 0, top: obj.top || 0 });
      }
    });

    // 2. Создаем ActiveSelection
    const activeSelection = new ActiveSelection(objects, {
      canvas: this.canvas,
    });

    // 3. ВОССТАНАВЛИВАЕМ оригинальные позиции СРАЗУ после создания

    // Находим границы группы для правильного позиционирования
    let minLeft = Infinity;
    let minTop = Infinity;
    let maxRight = -Infinity;
    let maxBottom = -Infinity;

    objects.forEach((obj) => {
      if (obj.id && originalPositions.has(obj.id)) {
        const pos = originalPositions.get(obj.id)!;
        // 🎯 КРИТИЧНО: Учитываем scaleX/scaleY для корректного размера bounding box
        // Картинки могут иметь width:1024, scaleX:0.017 → реальный размер ~17px
        const objWidth = (obj.width || 0) * (obj.scaleX || 1);
        const objHeight = (obj.height || 0) * (obj.scaleY || 1);

        minLeft = Math.min(minLeft, pos.left);
        minTop = Math.min(minTop, pos.top);
        maxRight = Math.max(maxRight, pos.left + objWidth);
        maxBottom = Math.max(maxBottom, pos.top + objHeight);
      }
    });

    const groupWidth = maxRight - minLeft;
    const groupHeight = maxBottom - minTop;

    // Восстанавливаем позиции относительно минимальных координат
    activeSelection.forEachObject((obj) => {
      if (obj.id && originalPositions.has(obj.id)) {
        const originalPos = originalPositions.get(obj.id)!;
        // Делаем позиции относительными к левому верхнему углу группы
        const relativeLeft = originalPos.left - minLeft - groupWidth / 2;
        const relativeTop = originalPos.top - minTop - groupHeight / 2;

        obj.set({
          left: relativeLeft,
          top: relativeTop,
        });
        obj.setCoords();
      }
    });

    // Устанавливаем позицию и размеры самой группы
    activeSelection.set({
      left: minLeft,
      top: minTop,
      width: groupWidth,
      height: groupHeight,
    });

    // Обновляем координаты группы
    activeSelection.setCoords();

    return activeSelection;
  }

  /**
   * Получить MetaBullet ID для любого объекта (TextEntity, Marker, или content)
   * @param obj - объект на канвасе
   * @returns metaBulletId или null если объект не принадлежит MetaBullet
   */
  private getMetaBulletIdForObject(obj: any): string | null {
    // 1. Проверяем TextEntity - у него есть прямое свойство metaBulletId
    if (TextEntity.isTextEntity(obj)) {
      return (obj as TextEntity).metaBulletId || null;
    }

    // 2. Проверяем Marker - ищем связанный TextEntity
    if (obj.isMarker) {
      const textEntityId = obj.textEntityId;
      if (textEntityId) {
        const textEntity = this.canvas.getObjects().find((o: any) => o.id === textEntityId);
        if (textEntity && TextEntity.isTextEntity(textEntity)) {
          return (textEntity as TextEntity).metaBulletId || null;
        }
      }
    }

    // 3. Проверяем content объект (image/shape) - ищем через markerId
    if (obj.markerId) {
      const marker = this.canvas.getObjects().find((o: any) => o.id === obj.markerId);
      if (marker && marker.isMarker) {
        return this.getMetaBulletIdForObject(marker); // Рекурсивно через маркер
      }
    }

    return null;
  }

  /**
   * Найти MetaBullet по координатам мыши
   */
  public findMetaBulletAtPoint(x: number, y: number): string | null {
    for (const [metaBulletId, metaBullet] of this.metaBullets.entries()) {
      const entities = metaBullet.getEntities();
      if (entities.length === 0) continue;

      // Проверяем попадание в область MetaBullet
      let minX = Infinity,
        minY = Infinity,
        maxX = -Infinity,
        maxY = -Infinity;

      entities.forEach((e) => {
        const left = e.left || 0;
        const top = e.top || 0;
        const right = left + (e.width || 0);
        const bottom = top + (e.height || 0);

        minX = Math.min(minX, left);
        minY = Math.min(minY, top);
        maxX = Math.max(maxX, right);
        maxY = Math.max(maxY, bottom);

        // Учитываем маркеры
        const linkedMarkers = this.getLinkedMarkers(e);
        linkedMarkers.forEach((marker) => {
          const markerLeft = marker.left || 0;
          const markerTop = marker.top || 0;
          const markerRight = markerLeft + (marker.width || 0);
          const markerBottom = markerTop + (marker.height || 0);

          minX = Math.min(minX, markerLeft);
          minY = Math.min(minY, markerTop);
          maxX = Math.max(maxX, markerRight);
          maxY = Math.max(maxY, markerBottom);
        });
      });

      // Добавляем padding для более удобного наведения
      const padding = 10;
      minX -= padding;
      minY -= padding;
      maxX += padding;
      maxY += padding;

      // Проверяем попадание точки в область
      if (x >= minX && x <= maxX && y >= minY && y <= maxY) {
        return metaBulletId;
      }
    }

    return null;
  }

  /**
   * Получить статистику системы для отладки
   */
  getDebugInfo(): {
    canvasId: string;
    isInitialized: boolean;
    metaBulletsCount: number;
    totalEntities: number;
    metaBullets: string[];
  } {
    const metaBullets = Array.from(this.metaBullets.values());

    return {
      canvasId: this.canvasId,
      isInitialized: this.isInitialized,
      metaBulletsCount: metaBullets.length,
      totalEntities: metaBullets.reduce((sum, mb) => sum + mb.entityIds.length, 0),
      metaBullets: metaBullets.map((mb) => mb.getDebugInfo()),
    };
  }

  // ========================================
  // НОВЫЕ МЕТОДЫ ДЛЯ ACTIVESELECTION
  // ========================================

  /**
   * Обработчик создания выделения (selection:created)
   */
  private handleSelectionCreated(): void {
    const activeSelection = this.canvas.getActiveObject();
    if (!activeSelection) {
      return;
    }

    // === УМНОЕ ВЫДЕЛЕНИЕ MetaBullets ===
    // Если выделено 2+ объекта из одного MetaBullet, или объект из MetaBullet + другой объект,
    // то выделяем ВЕСЬ MetaBullet целиком

    // 🚫 ЗАЩИТА ОТ РЕКУРСИИ: Если уже обрабатываем умное выделение, пропускаем
    if (this.isProcessingSmartSelection) {
      // Продолжаем обычную обработку (маркеры и т.д.)
      const onlyMarkers = isSelectionOnlyMarkers(activeSelection);
      const hasMixedMarkers = hasMarkersInMixedSelection(activeSelection);

      if (onlyMarkers) {
        lockMarkerTransformations(activeSelection);
        this.canvas.requestRenderAll();
        return;
      }

      if (hasMixedMarkers) {
        lockMarkerTransformations(activeSelection);
        this.canvas.requestRenderAll();
      }

      // Обрабатываем TextEntities
      if (activeSelection.type.toLowerCase() === 'activeselection') {
        const selectedObjects = (activeSelection as any)._objects || [];
        const textEntities = selectedObjects.filter((obj: any) => TextEntity.isTextEntity(obj));

        if (textEntities.length > 0) {
          this.activeSelectionEntities.clear();
          textEntities.forEach((entity: any) => {
            this.activeSelectionEntities.add(entity.id);
          });

          textEntities.forEach((entity: any) => {
            if (entity.updateMarkerPositionSync) {
              entity.updateMarkerPositionSync();
            } else if (entity.updateMarkerPosition) {
              entity.updateMarkerPosition();
            }
          });
        }
      }
      return;
    }

    if (activeSelection.type.toLowerCase() === 'activeselection') {
      const selectedObjects = (activeSelection as any)._objects || [];
      if (selectedObjects.length > 1) {
        // Группируем объекты по metaBulletId
        const objectsByMetaBullet = new Map<string | null, any[]>();
        selectedObjects.forEach((obj: any) => {
          const metaBulletId = this.getMetaBulletIdForObject(obj);
          if (!objectsByMetaBullet.has(metaBulletId)) {
            objectsByMetaBullet.set(metaBulletId, []);
          }
          objectsByMetaBullet.get(metaBulletId)!.push(obj);
        });

        // Проверяем условия для автоматического выделения всего MetaBullet
        let shouldSelectWholeMetaBullets = false;
        const metaBulletsToSelect: string[] = [];

        // Условие 1: Есть 2+ объекта из одного MetaBullet
        for (const [metaBulletId, objects] of objectsByMetaBullet.entries()) {
          if (metaBulletId && objects.length >= 2) {
            shouldSelectWholeMetaBullets = true;
            metaBulletsToSelect.push(metaBulletId);
          }
        }

        // Условие 2: Есть объект из MetaBullet + объект не из MetaBullet
        const hasMetaBulletObjects = Array.from(objectsByMetaBullet.keys()).some(
          (id) => id !== null,
        );
        const hasNonMetaBulletObjects = objectsByMetaBullet.has(null);

        if (hasMetaBulletObjects && hasNonMetaBulletObjects) {
          shouldSelectWholeMetaBullets = true;
          // Добавляем все MetaBullets в список для выделения
          for (const [metaBulletId] of objectsByMetaBullet.entries()) {
            if (metaBulletId && !metaBulletsToSelect.includes(metaBulletId)) {
              metaBulletsToSelect.push(metaBulletId);
            }
          }
        }

        // Если нужно выделить целые MetaBullets - делаем это
        if (shouldSelectWholeMetaBullets && metaBulletsToSelect.length > 0) {
          // Собираем все объекты из всех MetaBullets
          const allMetaBulletObjects: any[] = [];
          metaBulletsToSelect.forEach((metaBulletId) => {
            const metaBullet = this.metaBullets.get(metaBulletId);
            if (metaBullet) {
              const entities = metaBullet.getEntities();

              // Добавляем все entities
              allMetaBulletObjects.push(...entities);

              // Добавляем все маркеры и их контент
              entities.forEach((entity) => {
                const markers = this.getLinkedMarkers(entity);
                allMetaBulletObjects.push(...markers);

                markers.forEach((marker) => {
                  const contentObjects = this.canvas
                    .getObjects()
                    .filter((obj: any) => obj.markerId === marker.id);
                  allMetaBulletObjects.push(...contentObjects);
                });
              });
            }
          });

          // Добавляем объекты не из MetaBullet (если есть)
          if (hasNonMetaBulletObjects) {
            const nonMetaBulletObjects = objectsByMetaBullet.get(null) || [];
            allMetaBulletObjects.push(...nonMetaBulletObjects);
          }

          // ✅ УСТАНАВЛИВАЕМ ФЛАГ перед созданием нового выделения
          this.isProcessingSmartSelection = true;

          try {
            // Создаем новое ActiveSelection со всеми объектами
            this.canvas.discardActiveObject();

            // 🎯 КРИТИЧНО: Используем helper с фиксом позиций чтобы объекты не "улетали"
            const newActiveSelection =
              this.createActiveSelectionWithPositionFix(allMetaBulletObjects);

            this.canvas.setActiveObject(newActiveSelection);
            this.canvas.requestRenderAll();
          } finally {
            // ✅ СБРАСЫВАЕМ ФЛАГ после завершения
            this.isProcessingSmartSelection = false;
          }

          // Событие selection:created сработает автоматически и обработается
          // с флагом isProcessingSmartSelection=false, так что обычная логика применится
          return;
        }
      }
    }

    // Проверяем является ли выделение только маркерами
    const onlyMarkers = isSelectionOnlyMarkers(activeSelection);
    const hasMixedMarkers = hasMarkersInMixedSelection(activeSelection);

    if (onlyMarkers) {
      // Блокируем все трансформации для группы только из маркеров
      lockMarkerTransformations(activeSelection);
      this.canvas.requestRenderAll();
      return;
    }

    // Если есть маркеры в смешанном выделении - блокируем масштабирование и поворот
    if (hasMixedMarkers) {
      lockMarkerTransformations(activeSelection);
      this.canvas.requestRenderAll();
      // Продолжаем обработку как обычно для TextEntity
    }

    // Проверяем что это ActiveSelection с несколькими объектами
    if (activeSelection.type.toLowerCase() !== 'activeselection') {
      return;
    }

    const selectedObjects = (activeSelection as any)._objects || [];
    const textEntities = selectedObjects.filter((obj: any) => TextEntity.isTextEntity(obj));

    if (textEntities.length === 0) {
      return;
    }

    // Сохраняем ID выделенных сущностей
    this.activeSelectionEntities.clear();
    textEntities.forEach((entity: any) => {
      this.activeSelectionEntities.add(entity.id);
    });

    // 🎯 УПРОЩЕНИЕ: Не скрываем маркеры, показываем все всегда
    // Только обновляем позиции маркеров для выделенных объектов СИНХРОННО
    textEntities.forEach((entity: any) => {
      if (entity.updateMarkerPositionSync) {
        entity.updateMarkerPositionSync();
      } else if (entity.updateMarkerPosition) {
        entity.updateMarkerPosition();
      }
    });
  }

  /**
   * Обработчик обновления выделения (selection:updated)
   */
  private handleSelectionUpdated(): void {
    const activeSelection = this.canvas.getActiveObject();
    if (!activeSelection) {
      this.activeSelectionEntities.clear();
      return;
    }

    // === УМНОЕ ВЫДЕЛЕНИЕ MetaBullets ===
    // Если выделено 2+ объекта из одного MetaBullet, или объект из MetaBullet + другой объект,
    // то выделяем ВЕСЬ MetaBullet целиком

    // 🚫 ЗАЩИТА ОТ РЕКУРСИИ: Если уже обрабатываем умное выделение, пропускаем
    if (this.isProcessingSmartSelection) {
      // Продолжаем обычную обработку (маркеры и т.д.)
      const onlyMarkers = isSelectionOnlyMarkers(activeSelection);
      const hasMixedMarkers = hasMarkersInMixedSelection(activeSelection);

      if (onlyMarkers) {
        lockMarkerTransformations(activeSelection);
        this.canvas.requestRenderAll();
        this.activeSelectionEntities.clear();
        return;
      }

      if (hasMixedMarkers) {
        lockMarkerTransformations(activeSelection);
        this.canvas.requestRenderAll();
      }

      // Обрабатываем TextEntities
      if (activeSelection.type.toLowerCase() === 'activeselection') {
        const selectedObjects = (activeSelection as any)._objects || [];
        const textEntities = selectedObjects.filter((obj: any) => TextEntity.isTextEntity(obj));

        this.activeSelectionEntities.clear();
        textEntities.forEach((entity: any) => {
          this.activeSelectionEntities.add(entity.id);
        });

        textEntities.forEach((entity: any) => {
          if (entity.updateMarkerPositionSync) {
            entity.updateMarkerPositionSync();
          } else if (entity.updateMarkerPosition) {
            entity.updateMarkerPosition();
          }
        });
      }
      return;
    }

    if (activeSelection.type.toLowerCase() === 'activeselection') {
      const selectedObjects = (activeSelection as any)._objects || [];
      if (selectedObjects.length > 1) {
        // Группируем объекты по metaBulletId
        const objectsByMetaBullet = new Map<string | null, any[]>();
        selectedObjects.forEach((obj: any) => {
          const metaBulletId = this.getMetaBulletIdForObject(obj);
          if (!objectsByMetaBullet.has(metaBulletId)) {
            objectsByMetaBullet.set(metaBulletId, []);
          }
          objectsByMetaBullet.get(metaBulletId)!.push(obj);
        });

        // Проверяем условия для автоматического выделения всего MetaBullet
        let shouldSelectWholeMetaBullets = false;
        const metaBulletsToSelect: string[] = [];

        // Условие 1: Есть 2+ объекта из одного MetaBullet
        for (const [metaBulletId, objects] of objectsByMetaBullet.entries()) {
          if (metaBulletId && objects.length >= 2) {
            shouldSelectWholeMetaBullets = true;
            metaBulletsToSelect.push(metaBulletId);
          }
        }

        // Условие 2: Есть объект из MetaBullet + объект не из MetaBullet
        const hasMetaBulletObjects = Array.from(objectsByMetaBullet.keys()).some(
          (id) => id !== null,
        );
        const hasNonMetaBulletObjects = objectsByMetaBullet.has(null);

        if (hasMetaBulletObjects && hasNonMetaBulletObjects) {
          shouldSelectWholeMetaBullets = true;
          // Добавляем все MetaBullets в список для выделения
          for (const [metaBulletId] of objectsByMetaBullet.entries()) {
            if (metaBulletId && !metaBulletsToSelect.includes(metaBulletId)) {
              metaBulletsToSelect.push(metaBulletId);
            }
          }
        }

        // Если нужно выделить целые MetaBullets - делаем это
        if (shouldSelectWholeMetaBullets && metaBulletsToSelect.length > 0) {
          // Собираем все объекты из всех MetaBullets
          const allMetaBulletObjects: any[] = [];
          metaBulletsToSelect.forEach((metaBulletId) => {
            const metaBullet = this.metaBullets.get(metaBulletId);
            if (metaBullet) {
              const entities = metaBullet.getEntities();

              // Добавляем все entities
              allMetaBulletObjects.push(...entities);

              // Добавляем все маркеры и их контент
              entities.forEach((entity) => {
                const markers = this.getLinkedMarkers(entity);
                allMetaBulletObjects.push(...markers);

                markers.forEach((marker) => {
                  const contentObjects = this.canvas
                    .getObjects()
                    .filter((obj: any) => obj.markerId === marker.id);
                  allMetaBulletObjects.push(...contentObjects);
                });
              });
            }
          });

          // Добавляем объекты не из MetaBullet (если есть)
          if (hasNonMetaBulletObjects) {
            const nonMetaBulletObjects = objectsByMetaBullet.get(null) || [];
            allMetaBulletObjects.push(...nonMetaBulletObjects);
          }

          // ✅ УСТАНАВЛИВАЕМ ФЛАГ перед созданием нового выделения
          this.isProcessingSmartSelection = true;

          try {
            // Создаем новое ActiveSelection со всеми объектами
            this.canvas.discardActiveObject();

            // 🎯 КРИТИЧНО: Используем helper с фиксом позиций чтобы объекты не "улетали"
            const newActiveSelection =
              this.createActiveSelectionWithPositionFix(allMetaBulletObjects);

            this.canvas.setActiveObject(newActiveSelection);
            this.canvas.requestRenderAll();
          } finally {
            // ✅ СБРАСЫВАЕМ ФЛАГ после завершения
            this.isProcessingSmartSelection = false;
          }

          // Событие selection:updated сработает автоматически и обработается
          // с флагом isProcessingSmartSelection=false, так что обычная логика применится
          return;
        }
      }
    }

    // Проверяем является ли выделение только маркерами
    const onlyMarkers = isSelectionOnlyMarkers(activeSelection);
    const hasMixedMarkers = hasMarkersInMixedSelection(activeSelection);

    if (onlyMarkers) {
      // Блокируем все трансформации для группы только из маркеров
      lockMarkerTransformations(activeSelection);
      this.canvas.requestRenderAll();
      this.activeSelectionEntities.clear();
      return;
    }

    // Если есть маркеры в смешанном выделении - блокируем масштабирование и поворот
    if (hasMixedMarkers) {
      lockMarkerTransformations(activeSelection);
      this.canvas.requestRenderAll();
      // Продолжаем обработку как обычно для TextEntity
    }

    // Проверяем что это ActiveSelection с несколькими объектами
    if (activeSelection.type.toLowerCase() !== 'activeselection') {
      this.activeSelectionEntities.clear();
      return;
    }

    const selectedObjects = (activeSelection as any)._objects || [];
    const textEntities = selectedObjects.filter((obj: any) => TextEntity.isTextEntity(obj));

    // Обновляем список выделенных сущностей
    this.activeSelectionEntities.clear();
    textEntities.forEach((entity: any) => {
      this.activeSelectionEntities.add(entity.id);
    });

    //  Не скрываем маркеры, показываем все всегда
    // Только обновляем позиции маркеров для выделенных объектов СИНХРОННО
    textEntities.forEach((entity: any) => {
      if (entity.updateMarkerPositionSync) {
        entity.updateMarkerPositionSync();
      } else if (entity.updateMarkerPosition) {
        entity.updateMarkerPosition();
      }
    });
  }

  /**
   * Обработчик очистки выделения (selection:cleared)
   */
  private handleSelectionCleared(): void {
    // 🎯 ОТКЛЮЧАЕМ режим ActiveSelection
    if (this.isActiveSelectionMode) {
      this.isActiveSelectionMode = false;

      // 🎯 ВОССТАНАВЛИВАЕМ позиции всех MetaBullet после снятия ActiveSelection
      this.restoreMetaBulletPositionsAfterActiveSelection();
    }

    this.isActiveSelectionMoving = false;

    //  Не нужно восстанавливать видимость - все маркеры всегда видны

    // Очищаем список выделенных сущностей с небольшой задержкой
    // Это нужно для предотвращения ложного удаления объектов
    setTimeout(() => {
      this.activeSelectionEntities.clear();
    }, 100);
  }

  /**
   * Найти маркер для конкретной сущности
   */
  private findMarkerForEntity(entityId: string): Marker | null {
    const allCanvasObjects = this.canvas.getObjects();
    return (
      (allCanvasObjects.find(
        (obj) => obj instanceof Marker && obj.entityId === entityId,
      ) as Marker) || null
    );
  }

  /**
   * Обновить позиции маркеров для ActiveSelection
   */
  private updateMarkersForActiveSelection(): void {
    if (this.activeSelectionEntities.size === 0) {
      return;
    }

    this.activeSelectionEntities.forEach((entityId) => {
      const entity = this.findEntityById(entityId);
      if (entity && entity.updateMarkerPosition) {
        // Используем синхронную версию для ActiveSelection
        if (entity.updateMarkerPositionSync) {
          entity.updateMarkerPositionSync();
        } else {
          console.warn('updateMarkerPositionSync не доступен, используем обычный метод');
          entity.updateMarkerPosition();
        }
      } else {
        console.warn('🎯 ACTIVESELECTION: Entity not found or missing updateMarkerPosition:', {
          entityId,
          entityFound: !!entity,
          hasUpdateMethod: !!(entity && entity.updateMarkerPosition),
        });
      }
    });
  }

  /**
   * Найти TextEntity по ID
   */
  /**
   * Обновить позицию digit на основе позиции superscript (обратное позиционирование)
   */
  private updateDigitPositionFromSuperscript(superscript: TextEntity, digit: TextEntity): void {
    // Получаем конфигурацию digital-accent через публичный метод digit
    const digitalAccentConfig = digit.getDigitalAccentConfig?.();

    if (!digitalAccentConfig) {
      console.warn('⚠️ Digital-accent config not found for reverse positioning');
      return;
    }

    // Рассчитываем позицию digit на основе superscript
    const superscriptLeft = superscript.left || 0;
    const superscriptTop = superscript.top || 0;
    const superscriptHeight = superscript.height || 0;
    const digitHeight = digit.height || 0;
    const offsetY = digitalAccentConfig.superscript.offsetY;

    // Обратные расчеты: digit должен быть левее и ниже superscript
    const digitLeft =
      superscriptLeft - (digit.width || 0) - digitalAccentConfig.superscript.offsetX;

    let digitTop: number;

    if (typeof offsetY === 'number') {
      digitTop = superscriptTop - offsetY;
    } else if (offsetY === 'top') {
      digitTop = superscriptTop;
    } else if (offsetY === 'center') {
      digitTop = superscriptTop - (digitHeight - superscriptHeight) / 2;
    } else if (offsetY === 'bottom') {
      digitTop = superscriptTop - digitHeight + superscriptHeight;
    } else if (offsetY === 'centered-by-first-line') {
      const fontSize = digit.fontSize || 16;
      const lineHeight = digit.lineHeight || 1.16;
      const firstLineHeight = fontSize * lineHeight;
      digitTop = superscriptTop - (firstLineHeight - superscriptHeight) / 2;
    } else {
      digitTop = superscriptTop;
    }

    // Обновляем позицию digit
    digit.set({
      left: digitLeft,
      top: digitTop,
    });
    digit.setCoords();

    // Обновляем canvas
    if (this.canvas) {
      this.canvas.requestRenderAll();
    }
  }

  private findEntityById(entityId: string): TextEntity | null {
    const allCanvasObjects = this.canvas.getObjects();
    return (
      (allCanvasObjects.find(
        (obj) => TextEntity.isTextEntity(obj) && obj.id === entityId,
      ) as TextEntity) || null
    );
  }
}
